import { createNativeSensorDiagnostic, nativeSensorDiagnosticsAvailable } from "../native/sensorDiagnosticsBridge";

// Unmodified browser-provided values, not a claim of calibrated hardware output.
// Device axes are preserved. Screen angle is metadata, never a vehicle transform.
export const SENSOR_TESTS = {
  acceleration: { label: "Acceleration", unit: "m/s²", movements: ["stationary", "slide_x", "slide_y", "slide_z"] },
  rotation_rate: { label: "Rotation rate", unit: "°/s", movements: ["stationary", "rotate_alpha", "rotate_beta", "rotate_gamma"] },
  orientation: { label: "Orientation", unit: "°", movements: ["stationary", "rotate_alpha", "rotate_beta", "rotate_gamma"] },
};

export const MOVEMENT_LABELS = {
  stationary: "Keep the phone still",
  slide_x: "Move along the phone's left–right axis",
  slide_y: "Move along the phone's bottom–top axis",
  slide_z: "Move perpendicular to the screen",
  rotate_alpha: "Rotate around the axis perpendicular to the screen",
  rotate_beta: "Rotate around the phone's left–right axis",
  rotate_gamma: "Rotate around the phone's bottom–top axis",
};

const finite = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
const nonNegative = (value) => finite(value) !== null && value >= 0 ? value : null;
const vector = (value, axes) => Object.fromEntries(axes.map((axis) => [axis, finite(value?.[axis])]));

export function readSensorSample(sensor, event) {
  if (sensor === "acceleration") return {
    acceleration: vector(event.acceleration, ["x", "y", "z"]),
    acceleration_including_gravity: vector(event.accelerationIncludingGravity, ["x", "y", "z"]),
    interval_ms: nonNegative(event.interval),
  };
  if (sensor === "rotation_rate") return {
    rotation_rate: vector(event.rotationRate, ["alpha", "beta", "gamma"]),
    interval_ms: nonNegative(event.interval),
  };
  return {
    orientation: vector(event, ["alpha", "beta", "gamma"]),
    absolute: typeof event.absolute === "boolean" ? event.absolute : null,
  };
}

export function hasSensorValues(sensor, sample) {
  return Object.values(sample?.[sensor] || {}).some((value) => finite(value) !== null);
}

export function createSensorDiagnostic({ sensor, movement, mount, onSample, onFinish, env } = {}) {
  const root = env || window;
  // In the Capacitor shell, prefer the shared native bridge. Tests and plain
  // browsers keep using the bounded browser DeviceMotion/Orientation path.
  if (!env && nativeSensorDiagnosticsAvailable(root)) {
    return createNativeSensorDiagnostic({
      sensor, movement, mount, onSample, onFinish, host: root,
    });
  }
  env = root;
  const orientation = sensor === "orientation";
  const eventName = orientation ? "deviceorientation" : "devicemotion";
  const api = orientation ? env.DeviceOrientationEvent : env.DeviceMotionEvent;
  let active = false;
  let cancelled = false;
  let started = false;
  let timer;
  let origin;
  let lastSampleAt = -Infinity;
  let capture;

  function stop(reason = "user_stopped") {
    cancelled = true;
    if (!active) return;
    active = false;
    env.clearTimeout(timer);
    env.removeEventListener(eventName, receive);
    env.document.removeEventListener("visibilitychange", visibility);
    // Background suspension is a gap, never silently bridged or claimed continuous.
    capture.duration_ms = Math.min(60000, Math.max(0, env.performance.now() - origin));
    capture.stop_reason = reason;
    onFinish(capture);
  }

  function visibility() {
    if (env.document.visibilityState !== "visible") stop("page_hidden");
  }

  function receive(event) {
    if (!active || event.isTrusted !== true) return;
    const elapsed = env.performance.now() - origin;
    if (elapsed >= 20000) { stop("completed"); return; }
    if (elapsed < 0 || elapsed - lastSampleAt < 50) return;
    lastSampleAt = elapsed;
    const sample = {
      sequence: capture.samples.length,
      elapsed_ms: elapsed,
      device_epoch_ms: capture.started_device_epoch_ms + elapsed,
      event_timestamp_ms: nonNegative(event.timeStamp),
      screen_angle: finite(env.screen?.orientation?.angle ?? env.orientation),
      ...readSensorSample(sensor, event),
    };
    capture.samples.push(sample);
    onSample(sample, capture.samples.length);
    if (capture.samples.length >= 400) stop("sample_limit");
  }

  async function start() {
    if (started || cancelled) throw new Error("Start a new sensor check.");
    started = true;
    if (!SENSOR_TESTS[sensor]?.movements.includes(movement)) throw new Error("Choose a sensor and movement.");
    if (!env.isSecureContext) throw new Error("Sensor checks need a secure HTTPS connection.");
    if (!api) throw new Error("This browser does not offer this sensor.");
    if (env.document.visibilityState !== "visible") throw new Error("Keep NDG visible during the check.");
    // Must be invoked directly from the user's click, before any network await.
    const permission = typeof api.requestPermission === "function" ? await api.requestPermission() : "not_required";
    if (cancelled) return false;
    if (!["granted", "not_required"].includes(permission)) throw new Error("Sensor permission was not granted. No readings were taken.");
    if (env.document.visibilityState !== "visible") throw new Error("Keep NDG visible during the check.");
    origin = env.performance.now();
    capture = {
      capture_id: env.crypto.randomUUID(), sensor, movement, mount, permission,
      started_device_epoch_ms: Date.now(),
      time_origin_ms: nonNegative(env.performance.timeOrigin),
      duration_ms: 0, stop_reason: "completed", samples: [],
    };
    active = true;
    env.addEventListener(eventName, receive);
    env.document.addEventListener("visibilitychange", visibility);
    timer = env.setTimeout(() => stop("completed"), 20000);
    return true;
  }

  return { start, stop };
}

export function captureMatchesRecord(capture, record) {
  // Compare every submitted field, including nulls, without depending on key order.
  const canonical = (value) => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === "object") return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
    return value;
  };
  return record?.validation_status === "unverified"
    && record?.teaching_eligible === false
    && JSON.stringify(canonical(record?.capture)) === JSON.stringify(canonical(capture));
}
